import { ChevronLeft, ChevronRight } from "lucide-react";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) => {
  const getPageNumbers = () => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage < 4) {
      return [1, 2, 3, 4, "...", totalPages];
    }

    if (currentPage > totalPages - 3) {
      return [
        1,
        "...",
        ...Array.from({ length: 4 }, (_, i) => totalPages - 3 + i),
      ];
    }

    return [1, "...", currentPage - 1, currentPage, "...", totalPages];
  };

  const handlePrevious = () => {
    if (currentPage <= 1) return;

    onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage >= totalPages) return;

    onPageChange(currentPage + 1);
  };

  if (totalPages <= 1) return null;

  return (
    <div
      dir="ltr"
      className="flex items-center justify-center gap-2 md:gap-4 w-full"
    >
      {/* Previous Button */}
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={`flex items-center justify-center p-[10px] rounded-[4px]text-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
        aria-label="Previous Page"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-4">
        {getPageNumbers().map((page, i) => {
          if (page === "...") {
            return (
              <button
                key={`ellipsis-${i}`}
                disabled
                className={`flex items-center justify-center size-8 p-[10px] rounded-[4px] border button1-text transition-all disabled:border-primary`}
              >
                ...
              </button>
            );
          }

          const pageNumber = page as number;
          const isActive = pageNumber === currentPage;

          return (
            <button
              key={pageNumber}
              onClick={() => {
                if (pageNumber === currentPage) return;
                onPageChange(pageNumber);
              }}
              className={`flex items-center justify-center p-[10px] min-w-8 h-8 rounded-[4px] border button1-text transition-all cursor-pointer ${
                isActive
                  ? "bg-primary text-white border-primary"
                  : "bg-transparent text-primary border-primary hover:bg-primary/10"
              }`}
            >
              {pageNumber}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className={`flex items-center justify-center p-[10px] rounded-[4px] text-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
        aria-label="Next Page"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Pagination;
